/// Aqui é o componente de gerenciamento de hábitos
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Paper,
  Typography,
  Switch,
  Tooltip,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
  Grid,
  Card
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { getCurrentDateTime } from '../utils/dateUtils';
import { v4 as uuidv4 } from 'uuid';
import { doc, updateDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/firebase';
import { getContrastText } from '../utils/colorUtils';
import { usePlan } from '../contexts/PlanContext';

const WEEKDAYS = [
  { id: 1, name: 'Segunda', short: 'Seg' },
  { id: 2, name: 'Terça', short: 'Ter' },
  { id: 3, name: 'Quarta', short: 'Qua' },
  { id: 4, name: 'Quinta', short: 'Qui' },
  { id: 5, name: 'Sexta', short: 'Sex' },
  { id: 6, name: 'Sábado', short: 'Sáb' },
  { id: 7, name: 'Domingo', short: 'Dom' }
];

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

function calculateStats(habit) {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

  // Calcula quantos dias do mês atual o hábito está programado
  let programmedDays = 0;
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(currentYear, currentMonth, day);
    const weekDay = date.getDay();
    const adjustedWeekDay = weekDay === 0 ? 7 : weekDay;
    
    if (!habit.weekdays?.length || habit.weekdays.includes(adjustedWeekDay)) {
      programmedDays++;
    }
  }

  // Calcula quantos dias do mês atual o hábito foi realizado
  const completedThisMonth = habit.completedDates?.filter(date => {
    try {
      // Normaliza a data removendo a parte do horário
      const dateStr = date.split(' - ')[0];
      const [day, month, year] = dateStr.split('/').map(Number);
      const completedDate = new Date(year, month - 1, day);
      
      return completedDate.getMonth() === currentMonth &&
             completedDate.getFullYear() === currentYear;
    } catch (error) {
      console.error('Erro ao processar data:', date, error);
      return false;
    }
  }).length || 0;

  return { total: programmedDays, thisMonth: completedThisMonth };
}

function formatWeekdays(weekdays) {
  if (!weekdays || weekdays.length === 0) return 'Todos os dias';
  
  // Verifica se TODOS os dias da semana estão selecionados
  const allDays = [1, 2, 3, 4, 5, 6, 7];
  const hasAllDays = allDays.every(day => weekdays.includes(day));
  if (hasAllDays) return 'Todos os dias';
  
  // Verifica se são dias úteis (segunda a sexta)
  if (weekdays.length === 5 && 
      weekdays.includes(1) && 
      weekdays.includes(2) && 
      weekdays.includes(3) && 
      weekdays.includes(4) && 
      weekdays.includes(5)) return 'Seg a Sex';
  
  return weekdays
    .sort((a, b) => {
      // Reordenar para que domingo (7) venha primeiro
      const adjustA = a === 7 ? 0 : a;
      const adjustB = b === 7 ? 0 : b;
      return adjustA - adjustB;
    })
    .map(day => WEEKDAYS.find(d => d.id === day)?.short)
    .join(', ');
}

function StatCard({ icon, title, value, color = 'primary', customColors }) {
  return (
    <Card sx={{ 
      p: 2, 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      borderRadius: 2,
      boxShadow: 2,
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 4
      }
    }}>
      <Box sx={{ 
        color: customColors?.primary || color, 
        mb: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {React.cloneElement(icon, { 
          sx: { fontSize: { xs: 32, sm: 40 } }
        })}
      </Box>
      <Typography 
        variant="h6" 
        component="div" 
        gutterBottom 
        sx={{ 
          fontSize: { xs: '0.875rem', sm: '1rem' },
          fontWeight: 'bold',
          lineHeight: 1.2,
          mb: 1,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}
      >
        {title}
      </Typography>
      <Typography 
        variant="h6" 
        sx={{ 
          color: customColors?.primary || 'primary.main',
          fontSize: { xs: '1rem', sm: '1.25rem' },
          fontWeight: 'bold'
        }}
      >
        {value}
      </Typography>
    </Card>
  );
}

// Novo componente para o diálogo de comparação de planos
function PlanComparisonDialog({ open, onClose, customColors }) {
  const theme = useTheme();
  const primaryColor = customColors?.primary || theme.palette.primary.main;
  const primaryTextColor = getContrastText(primaryColor);

  const planFeatures = {
    basic: [
      { text: 'Cadastrar até 3 hábitos', limit: '3' },
      { text: 'Cadastrar até 2 metas', limit: '2' },
      { text: 'Cadastrar até 1 desafio', limit: '1' },
      { text: 'Cadastrar até 3 notas', limit: '3' },
      { text: 'Acesso à aba Produtividade', available: false },
      { text: 'Acesso à aba Meditações', available: false },
      { text: 'Acesso à aba Estatísticas', available: false }
    ],
    pro: [
      { text: 'Hábitos ilimitados', unlimited: true },
      { text: 'Metas ilimitadas', unlimited: true },
      { text: 'Desafios ilimitados', unlimited: true },
      { text: 'Notas ilimitadas', unlimited: true },
      { text: 'Acesso à aba Produtividade', available: true },
      { text: 'Acesso à aba Meditações', available: true },
      { text: 'Acesso à aba Estatísticas', available: true }
    ]
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white'
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        bgcolor: primaryColor,
        color: primaryTextColor,
        py: 2
      }}>
        Limite do Plano Básico Atingido
      </DialogTitle>
      <DialogContent sx={{ py: 4 }}>
        <Grid container spacing={4}>
          {/* Coluna do Plano Básico */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ 
              p: 3, 
              height: '100%', 
              position: 'relative',
              bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white',
              border: `1px solid ${theme.palette.divider}`
            }}>
              <Typography variant="h6" gutterBottom sx={{ 
                textAlign: 'center',
                color: primaryColor,
                fontWeight: 'bold',
                mb: 3
              }}>
                Plano Básico
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {planFeatures.basic.map((feature, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {feature.available === false ? (
                      <CloseIcon color="error" />
                    ) : (
                      <CheckIcon sx={{ color: primaryColor }} />
                    )}
                    <Typography color="text.primary">
                      {feature.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>

          {/* Coluna do Plano PRO */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ 
              p: 3, 
              height: '100%',
              position: 'relative',
              bgcolor: primaryColor,
              color: primaryTextColor,
              '& .MuiTypography-root': {
                color: primaryTextColor
              }
            }}>
              <Typography variant="h6" gutterBottom sx={{ 
                textAlign: 'center',
                fontWeight: 'bold',
                mb: 3
              }}>
                Plano PRO
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {planFeatures.pro.map((feature, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CheckIcon sx={{ color: primaryTextColor }} />
                    <Typography>
                      {feature.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ 
        p: 3, 
        justifyContent: 'center',
        bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white'
      }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          sx={{ 
            mr: 2,
            borderColor: primaryColor,
            color: primaryColor,
            '&:hover': {
              borderColor: primaryColor,
              bgcolor: `${primaryColor}10`
            }
          }}
        >
          Continuar no Plano Básico
        </Button>
        <Button 
          variant="contained"
          sx={{ 
            bgcolor: primaryColor,
            color: primaryTextColor,
            '&:hover': {
              bgcolor: theme.palette.mode === 'dark'
                ? `${primaryColor}dd`
                : `${primaryColor}bb`
            }
          }}
        >
          Fazer Upgrade para PRO
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const HabitManagement = forwardRef(({ habits = [], onDeleteHabit, onUpdateHabit, onReorderHabits, onAddHabit, onDeleteAllHabits, customColors }, ref) => {
  const { currentUser } = useAuth();
  const [editingHabit, setEditingHabit] = useState(null);
  const [newName, setNewName] = useState('');
  const [selectedDays, setSelectedDays] = useState([]);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [isAddingHabit, setIsAddingHabit] = useState(false);
  const [isDeleteAllConfirmationOpen, setIsDeleteAllConfirmationOpen] = useState(false);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { userPlan } = usePlan();

  // Garante que habits seja um array e então ordena
  const sortedHabits = Array.isArray(habits) ? [...habits].sort((a, b) => {
    const orderA = typeof a.order === 'number' ? a.order : 0;
    const orderB = typeof b.order === 'number' ? b.order : 0;
    return orderA - orderB;
  }) : [];

  const handleMoveHabit = (currentIndex, direction) => {
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex >= 0 && newIndex < sortedHabits.length) {
      console.log('Moving habit:', { currentIndex, newIndex, direction });
      onReorderHabits(currentIndex, newIndex);
    }
  };

  const handleEditClick = (habit) => {
    setEditingHabit(habit);
    setNewName(habit.name);
    setSelectedDays(habit.weekdays || []);
  };

  const handleClose = () => {
    setEditingHabit(null);
    setNewName('');
    setSelectedDays([]);
  };

  const handleSave = async () => {
    const trimmedName = newName.trim();
    if (trimmedName && editingHabit) {
      // Verifica se já existe um hábito com este nome (exceto o próprio hábito sendo editado)
      const isDuplicate = habits.some(h => 
        h.id !== editingHabit.id && 
        String(h.name || '').toLowerCase() === trimmedName.toLowerCase()
      );

      // Verifica se o nome é muito curto (menos de 3 caracteres)
      if (trimmedName.length < 3) {
        alert('O nome do hábito deve ter pelo menos 3 caracteres.');
        return;
      }

      // Verifica se é um nome duplicado
      if (isDuplicate) {
        alert('Já existe um hábito com este nome.');
        return;
      }

      try {
        if (editingHabit) {
          // Chama onUpdateHabit para atualizar o hábito
          onUpdateHabit(editingHabit.id, {
            name: trimmedName,
            weekdays: selectedDays,
            active: selectedDays.length > 0 ? editingHabit.active : false,
            updatedAt: getCurrentDateTime()
          });
          handleClose();
        }
      } catch (error) {
        console.error('Erro ao salvar hábito:', error);
        alert('Erro ao salvar hábito. Por favor, tente novamente.');
      }
    }
  };

  const handleDayToggle = (dayId) => {
    setSelectedDays(prev => 
      prev.includes(dayId)
        ? prev.filter(d => d !== dayId)
        : [...prev, dayId].sort()
    );
  };

  const handleDeleteClick = (habit) => {
    setDeleteConfirmation(habit);
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirmation) {
      onDeleteHabit(deleteConfirmation.id);
      setDeleteConfirmation(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmation(null);
  };

  const handleAddClose = () => {
    setIsAddingHabit(false);
    setNewName('');
    setSelectedDays([]);
  };

  const handleAddClick = () => {
    // Verifica se o usuário está no plano básico e já tem 3 hábitos
    if (userPlan === 'BASIC' && habits.length >= 3) {
      setShowPlanDialog(true);
      return;
    }
    setIsAddingHabit(true);
    setNewName('');
    setSelectedDays(WEEKDAYS.map(day => day.id));
  };

  // Expõe a função handleAddClick através do ref
  useImperativeHandle(ref, () => ({
    handleAddClick: () => {
      // Verifica se o usuário está no plano básico e já tem 3 hábitos
      if (userPlan === 'BASIC' && habits.length >= 3) {
        setShowPlanDialog(true);
        return;
      }
      setIsAddingHabit(true);
      setNewName('');
      setSelectedDays(WEEKDAYS.map(day => day.id));
    }
  }));

  const handleAddSave = async () => {
    const trimmedName = newName.trim();
    if (trimmedName) {
      // Verifica se o usuário está no plano básico e já tem 3 hábitos
      if (userPlan === 'BASIC' && habits.length >= 3) {
        setShowPlanDialog(true);
        setIsAddingHabit(false);
        return;
      }

      // Verifica se já existe um hábito com este nome
      const isDuplicate = habits.some(h => 
        String(h.name || '').toLowerCase() === trimmedName.toLowerCase()
      );

      // Verifica se o nome é muito curto (menos de 3 caracteres)
      if (trimmedName.length < 3) {
        alert('O nome do hábito deve ter pelo menos 3 caracteres.');
        return;
      }

      // Verifica se é um nome duplicado
      if (isDuplicate) {
        alert('Já existe um hábito com este nome.');
        return;
      }

      // Criando novo hábito
      const newHabitWithId = {
        id: uuidv4(),
        name: trimmedName,
        weekdays: selectedDays,
        active: selectedDays.length > 0,
        createdAt: getCurrentDateTime(),
        updatedAt: getCurrentDateTime(),
        completedDates: [],
        order: habits.length > 0 ? habits[habits.length - 1].order + 1 : 0
      };

      try {
        await onAddHabit(newHabitWithId);
        handleAddClose();
      } catch (error) {
        console.error('Erro ao adicionar hábito:', error);
        alert('Erro ao adicionar hábito. Por favor, tente novamente.');
      }
    }
  };

  const handleDeleteAllClick = () => {
    setIsDeleteAllConfirmationOpen(true);
  };

  const handleDeleteAllConfirm = () => {
    if (onDeleteAllHabits) {
      onDeleteAllHabits();
    } else {
      // Fallback para o método antigo se onDeleteAllHabits não estiver disponível
      const habitsToDelete = [...habits];
      habitsToDelete.forEach(habit => {
        onDeleteHabit(habit.id);
      });
    }
    setIsDeleteAllConfirmationOpen(false);
  };

  const handleDeleteAllCancel = () => {
    setIsDeleteAllConfirmationOpen(false);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto' }}>
      {/* Cards de Estatísticas */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={6} sm={6} md={4}>
            <StatCard
              icon={<AssignmentIcon />}
              title="Total de Hábitos"
              value={habits.length}
              customColors={customColors}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={4}>
            <StatCard
              icon={<CheckCircleIcon />}
              title="Hábitos Ativos"
              value={habits.filter(habit => habit.active).length}
              color="success"
              customColors={customColors}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={4}>
            <StatCard
              icon={<BlockIcon />}
              title="Hábitos Inativos"
              value={habits.filter(habit => !habit.active).length}
              color="error"
              customColors={customColors}
            />
          </Grid>
        </Grid>
      </Box>

      <List>
        {sortedHabits.map((habit, index) => {
          const stats = calculateStats(habit);
          return (
            <Paper
              key={habit.id}
              elevation={1}
              sx={{
                mb: 2,
                bgcolor: 'background.paper',
                borderRadius: 1
              }}
            >
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  gap: { xs: 2, sm: 1 },
                  py: 2
                }}
              >
                <Box sx={{ 
                  flex: 1, 
                  minWidth: 0, 
                  display: 'flex', 
                  alignItems: 'center',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  width: '100%'
                }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: '1rem',
                      color: 'primary.main',
                      fontWeight: 500,
                      minWidth: 'fit-content',
                      textAlign: { xs: 'center', sm: 'left' }
                    }}
                  >
                    {habit.name}
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    justifyContent: { xs: 'center', sm: 'flex-start' },
                    width: { xs: '100%', sm: 'auto' }
                  }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ 
                        minWidth: 'fit-content',
                        textAlign: { xs: 'center', sm: 'left' }
                      }}
                    >
                      Frequência:
                    </Typography>
                    <Chip
                      size="small"
                      icon={<ScheduleIcon />}
                      label={formatWeekdays(habit.weekdays)}
                      sx={{ bgcolor: 'background.default' }}
                    />
                  </Box>
                </Box>
                <Box sx={{ 
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, 40px)',
                  gap: 1,
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: { xs: '100%', sm: 'auto' }
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Tooltip title="Ativar/Desativar">
                      <Switch
                        checked={habit.active}
                        onChange={() => onUpdateHabit(habit.id, { active: !habit.active })}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: customColors?.primary
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: customColors?.primary
                          }
                        }}
                      />
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Tooltip title="Editar">
                      <IconButton
                        onClick={() => handleEditClick(habit)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Tooltip title="Excluir">
                      <IconButton
                        onClick={() => handleDeleteClick(habit)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {index > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Tooltip title="Reordenar para cima">
                        <IconButton
                          onClick={() => handleMoveHabit(index, 'up')}
                          size="small"
                        >
                          <ArrowUpwardIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                  {index < sortedHabits.length - 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Tooltip title="Reordenar para baixo">
                        <IconButton
                          onClick={() => handleMoveHabit(index, 'down')}
                          size="small"
                        >
                          <ArrowDownwardIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              </ListItem>
            </Paper>
          );
        })}
      </List>

      {/* Edit Dialog */}
      <Dialog open={!!editingHabit} onClose={handleClose}>
        <DialogTitle>Editar Hábito</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome do hábito"
            type="text"
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <FormGroup sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Dias da semana
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {WEEKDAYS.map((day) => (
                <FormControlLabel
                  key={day.id}
                  control={
                    <Switch
                      checked={selectedDays.includes(day.id)}
                      onChange={() => handleDayToggle(day.id)}
                    />
                  }
                  label={day.name}
                />
              ))}
            </Box>
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Dialog */}
      <Dialog open={isAddingHabit} onClose={handleAddClose}>
        <DialogTitle>Novo Hábito</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome do hábito"
            type="text"
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <FormGroup sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Dias da semana
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {WEEKDAYS.map((day) => (
                <FormControlLabel
                  key={day.id}
                  control={
                    <Switch
                      checked={selectedDays.includes(day.id)}
                      onChange={() => handleDayToggle(day.id)}
                    />
                  }
                  label={day.name}
                />
              ))}
            </Box>
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddClose}>Cancelar</Button>
          <Button onClick={handleAddSave} variant="contained">
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirmation}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o hábito "{deleteConfirmation?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancelar</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Confirmation Dialog */}
      <Dialog
        open={isDeleteAllConfirmationOpen}
        onClose={() => setIsDeleteAllConfirmationOpen(false)}
      >
        <DialogTitle>Confirmar exclusão de todos os hábitos</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja remover todos os hábitos? Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteAllConfirmationOpen(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              onDeleteAllHabits();
              setIsDeleteAllConfirmationOpen(false);
            }}
            color="error"
            variant="contained"
          >
            Excluir Todos
          </Button>
        </DialogActions>
      </Dialog>

      {/* Plan Comparison Dialog */}
      <PlanComparisonDialog 
        open={showPlanDialog}
        onClose={() => setShowPlanDialog(false)}
        customColors={customColors}
      />
    </Box>
  );
});

export default HabitManagement; 